import React, { useState, useEffect } from "react";
import Layouts from "../../components/Layouts";
import {
  Button,
  Table,
  Space,
  Tag,
  message,
  Modal,
  Empty,
  Pagination,
  Input,
} from "antd";
import { Link } from "react-router-dom";
import { getDirectusClient } from "../../lib/directus";
import {
  readItems,
  updateItem,
  createItem,
  readUser,
  updateUser,
  readUsers,
} from "@directus/sdk";
import { useSelector } from "react-redux";
import validarpermiso from "../../lib/utils";

const { Search } = Input;

function TroopLoan() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const permisos = useSelector((state) => state.permisos);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [serialDetails, setSerialDetails] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  const getStatusColor = (status) => {
    const colors = {
      activo: "green",
      anulado: "red",
      cerrado: "gray",
      devolucion: "blue",
    };
    return colors[status] || "gold";
  };

  const fetchLoans = async (page = 1, pageSize = 10) => {
    if (!validarpermiso("prestamos", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();

      // Build filter object
      const filterObj = {
        _and: [
          {
            vendedor: {
              bodega: {
                id: { _eq: userBodega },
              },
            },
          },
        ],
      };

      // Add search filter if search text exists
      if (searchText) {
        filterObj._and.push({
          vendedor: {
            _or: [{ first_name: { _contains: searchText } }],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("prestamos", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get filtered and paginated data
      const response = await directus.request(
        readItems("prestamos", {
          fields: [
            "*",
            "vendedor.*",
            "seriales.seriales_id.*",
            "seriales.seriales_id.producto.*",
            "seriales.seriales_id.producto.grupo.*",
          ],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pageSize,
          offset: (page - 1) * pageSize,
        })
      );

      setLoans(response);
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
        total: totalItems,
      });
      setHasPermissionError(false);
    } catch (error) {
      console.error("Error fetching loans:", error);
      if (error.response?.status === 403) {
        setHasPermissionError(true);
        message.error("No tiene permisos para ver préstamos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const fetchSerialDetails = async (loan) => {
    try {
      const directus = await getDirectusClient();
      const serialPromises = loan.seriales.map(async (serial) => {
        const response = await directus.request(
          readItems("seriales", {
            fields: ["*", "producto.*"],
            filter: {
              id: { _eq: serial.seriales_id.id },
            },
          })
        );
        return response[0];
      });

      const serialsData = await Promise.all(serialPromises);
      setSerialDetails(serialsData);
    } catch (error) {
      console.error("Error fetching serial details:", error);
      message.error("Error al cargar los detalles de los seriales");
    }
  };

  const showLoanDetails = async (loan) => {
    setSelectedLoan(loan);
    setIsModalVisible(true);
    await fetchSerialDetails(loan);
  };

  const handleCancelLoan = async (loan) => {
    if (!validarpermiso("prestamos", "update", permisos)) {
      message.error("No tiene permisos para anular préstamos");
      return;
    }
    console.log(loan);
    // Calculate total amount to return
    let totalToReturn = 0;
    if (loan.seriales && loan.seriales.length > 0) {
      totalToReturn = loan.seriales.map((item) => {
        const precio = item.seriales_id.producto?.preciosinimp;
        const impuesto = item.seriales_id.producto?.grupo?.impuesto;
        return precio + (precio * impuesto) / 100;
      });
    }
    console.log("Total", totalToReturn);
    Modal.confirm({
      title: "¿Está seguro de anular este préstamo?",
      content:
        "Esta acción cambiará el estado del préstamo a anulado y los seriales volverán a estar activos",
      okText: "Sí, anular",
      cancelText: "No",
      onOk: async () => {
        try {
          const directus = await getDirectusClient();

          // Get vendor's current data
          const vendorResponse = await directus.request(
            readUser(loan.vendedor.id, {
              fields: ["id", "prestamo_usado", "first_name", "last_name"],
            })
          );

          // Update loan status
          await directus.request(
            updateItem("prestamos", loan.id, {
              estado: "anulado",
            })
          );

          // Update vendor's prestamo_usado
          await directus.request(
            updateUser(vendorResponse.id, {
              prestamo_usado:
                parseFloat(vendorResponse.prestamo_usado) - totalToReturn,
            })
          );

          // Update serials status and create history entries
          const serialPromises = loan.seriales.map(async (serial) => {
            await directus.request(
              updateItem("seriales", serial.seriales_id.id, {
                estado: 1,
                prestamo: null,
                user: null,
              })
            );

            // Create history entry for each serial
            await directus.request(
              createItem("historial_seriales", {
                tipo: 4, // tipo 4 for loan cancellation
                prestamo: loan.id,
                seriales: {
                  create: [
                    {
                      historial_seriales_id: "+",
                      seriales_id: {
                        id: serial.seriales_id.id,
                      },
                    },
                  ],
                },
              })
            );
          });

          await Promise.all(serialPromises);

          message.success(
            `Préstamo anulado exitosamente. Monto devuelto: $${totalToReturn.toLocaleString()}`
          );

          fetchLoans(pagination.current, pagination.pageSize); // Refresh the loans list
        } catch (error) {
          console.error("Error canceling loan:", error);
          message.error("Error al anular el préstamo");
        }
      },
    });
  };

  const handleSearch = async (value) => {
    try {
      setLoading(true);
      const directus = await getDirectusClient();

      // Build filter object with the value directly
      const filterObj = {
        _and: [
          {
            vendedor: {
              bodega: {
                id: { _eq: userBodega },
              },
            },
          },
        ],
      };

      // Add search filter using the value parameter
      if (value) {
        filterObj._and.push({
          vendedor: {
            _or: [
              { first_name: { _contains: value } },
              { last_name: { _contains: value } },
            ],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("prestamos", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get filtered data
      const response = await directus.request(
        readItems("prestamos", {
          fields: ["*", "vendedor.*", "seriales.*", "seriales.producto.*"],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pagination.pageSize,
          offset: 0,
        })
      );

      setSearchText(value);
      setLoans(response);
      setPagination({
        ...pagination,
        current: 1,
        total: totalItems,
      });
    } catch (error) {
      console.error("Error searching loans:", error);
      message.error("Error al buscar préstamos");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Vendedor",
      dataIndex: ["vendedor", "first_name"],
      key: "vendedor",
      render: (text, record) =>
        `${record.vendedor?.first_name || ""} ${
          record.vendedor?.last_name || ""
        }`,
    },
    {
      title: "Fecha",
      dataIndex: "date_created",
      key: "date",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => (
        <Tag color={getStatusColor(estado)}>{estado || "pendiente"}</Tag>
      ),
    },
    {
      title: "# Seriales",
      key: "seriales",
      render: (_, record) => record.seriales?.length || 0,
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space>
          {validarpermiso("prestamos", "read", permisos) && (
            <Button type="primary" onClick={() => showLoanDetails(record)}>
              Ver detalles
            </Button>
          )}
          {validarpermiso("prestamos", "update", permisos) &&
            (record.estado === "solicitud prestamo" ||
              record.estado === "solicitud_prestamo") && ( // Add this condition
              <Button
                type="primary"
                danger
                onClick={() => handleCancelLoan(record)}
              >
                Anular préstamo
              </Button>
            )}
        </Space>
      ),
    },
  ];
  console.log(selectedLoan);

  return (
    <Layouts>
      {!hasPermissionError ? (
        <>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space
              style={{
                marginBottom: 16,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
            
            <Space style={{ marginBottom: 16 }}>
              {validarpermiso("prestamos", "create", permisos) && (
                <Link to="/troop-loans_create">
                  <Button type="primary">Crear préstamo</Button>
                </Link>
              )}
              {validarpermiso("prestamos", "update", permisos) && (
                <Link to="/troop-loans_repay">
                  <Button>Devolver préstamo</Button>
                </Link>
              )}
            </Space>
              <Search
                placeholder="Buscar por vendedor"
                allowClear
                onSearch={handleSearch}
                style={{ width: 300 }}
                enterButton
              />
            </Space>

            <Table
              columns={columns}
              dataSource={loans}
              rowKey="id"
              loading={loading}
              pagination={false}
            />

            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={true}
              showQuickJumper={true}
              showTotal={(total) => `Total: ${total} préstamos`}
              pageSizeOptions={["10", "25", "50", "100"]}
              onChange={(page, pageSize) => {
                setPagination({
                  ...pagination,
                  current: page,
                  pageSize: pageSize,
                });
              }}
              style={{ marginTop: 16, textAlign: "right" }}
              disabled={!validarpermiso("prestamos", "read", permisos)}
            />
          </Space>
        </>
      ) : (
        <h3>No tiene permisos para ver préstamos</h3>
      )}
      <Modal
        title={`Detalles del Préstamo - ${selectedLoan?.vendedor?.first_name} ${selectedLoan?.vendedor?.last_name}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedLoan(null);
          setSerialDetails([]);
        }}
        footer={null}
        width={800}
      >
        {serialDetails.length > 0 ? (
          <Table
            dataSource={serialDetails}
            rowKey="id"
            columns={[
              {
                title: "Serial",
                dataIndex: "serial",
                key: "serial",
              },
              {
                title: "Producto",
                dataIndex: ["producto", "nombre"],
                key: "producto",
              },
              {
                title: "Estado",
                dataIndex: "estado",
                key: "estado",
                render: (estado) => {
                  let color = "gold";
                  let text = "Desconocido";

                  switch (estado) {
                    case 8:
                      color = "blue";
                      text = "Solicitud de préstamo";
                      break;
                    case 1:
                      color = "green";
                      text = "Activo";
                      break;
                    case 2:
                      color = "yellow";
                      text = "Facturado";
                      break;
                    default:
                      color = "gold";
                      text = "Otro";
                  }

                  return <Tag color={color}>{text}</Tag>;
                },
              },
            ]}
            pagination={false}
            scroll={{ y: 400 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No hay seriales asociados a este préstamo"
          />
        )}
      </Modal>
    </Layouts>
  );
}

export default TroopLoan;
